/**
 * Simple formula evaluator for custom metrics in the frontend
 * Supports: +, -, *, /, parentheses, and column references
 */

type TokenType = 'NUMBER' | 'IDENTIFIER' | 'OPERATOR' | 'LPAREN' | 'RPAREN';

interface Token {
  type: TokenType;
  value: string | number;
}

interface ASTNode {
  type: 'BinaryOp' | 'Number' | 'Identifier';
  operator?: string;
  left?: ASTNode;
  right?: ASTNode;
  value?: number | string;
}

function tokenize(formula: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < formula.length) {
    const char = formula[i];

    if (/\s/.test(char)) {
      i++;
      continue;
    }

    if (/[0-9.]/.test(char)) {
      let num = '';
      while (i < formula.length && /[0-9.]/.test(formula[i])) {
        num += formula[i++];
      }
      tokens.push({ type: 'NUMBER', value: parseFloat(num) });
      continue;
    }

    if (/[a-zA-Z_]/.test(char)) {
      let id = '';
      while (i < formula.length && /[a-zA-Z0-9_]/.test(formula[i])) {
        id += formula[i++];
      }
      tokens.push({ type: 'IDENTIFIER', value: id });
      continue;
    }

    if (['+', '-', '*', '/'].includes(char)) {
      tokens.push({ type: 'OPERATOR', value: char });
      i++;
      continue;
    }

    if (char === '(') {
      tokens.push({ type: 'LPAREN', value: '(' });
      i++;
      continue;
    }

    if (char === ')') {
      tokens.push({ type: 'RPAREN', value: ')' });
      i++;
      continue;
    }

    throw new Error(`Unexpected character: ${char}`);
  }

  return tokens;
}

function parseFormula(formula: string): ASTNode {
  const tokens = tokenize(formula);
  let pos = 0;

  function peek(): Token | undefined {
    return tokens[pos];
  }

  function consume(): Token {
    return tokens[pos++];
  }

  function parseExpression(): ASTNode {
    return parseAdditive();
  }

  function parseAdditive(): ASTNode {
    let left = parseMultiplicative();

    while (peek()?.type === 'OPERATOR' && ['+', '-'].includes(peek()!.value as string)) {
      const op = consume().value as string;
      const right = parseMultiplicative();
      left = { type: 'BinaryOp', operator: op, left, right };
    }

    return left;
  }

  function parseMultiplicative(): ASTNode {
    let left = parsePrimary();

    while (peek()?.type === 'OPERATOR' && ['*', '/'].includes(peek()!.value as string)) {
      const op = consume().value as string;
      const right = parsePrimary();
      left = { type: 'BinaryOp', operator: op, left, right };
    }

    return left;
  }

  function parsePrimary(): ASTNode {
    const token = peek();

    if (!token) {
      throw new Error('Unexpected end of formula');
    }

    if (token.type === 'NUMBER') {
      consume();
      return { type: 'Number', value: token.value as number };
    }

    if (token.type === 'IDENTIFIER') {
      consume();
      return { type: 'Identifier', value: token.value as string };
    }

    if (token.type === 'LPAREN') {
      consume();
      const expr = parseExpression();
      if (peek()?.type !== 'RPAREN') {
        throw new Error('Expected closing parenthesis');
      }
      consume();
      return expr;
    }

    throw new Error(`Unexpected token: ${token.value}`);
  }

  const ast = parseExpression();

  if (pos < tokens.length) {
    throw new Error(`Unexpected token after expression: ${tokens[pos].value}`);
  }

  return ast;
}

function evaluateAST(ast: ASTNode, context: Record<string, number>): number {
  switch (ast.type) {
    case 'Number':
      return ast.value as number;

    case 'Identifier': {
      const value = context[ast.value as string];
      if (value === undefined) {
        return 0; // Return 0 for missing columns
      }
      return value;
    }

    case 'BinaryOp': {
      const left = evaluateAST(ast.left!, context);
      const right = evaluateAST(ast.right!, context);

      switch (ast.operator) {
        case '+':
          return left + right;
        case '-':
          return left - right;
        case '*':
          return left * right;
        case '/':
          if (right === 0) return 0;
          return left / right;
        default:
          throw new Error(`Unknown operator: ${ast.operator}`);
      }
    }

    default:
      throw new Error(`Unknown AST node type: ${ast.type}`);
  }
}

/**
 * Evaluate a formula string with the given context values
 */
export function evaluateFormula(formula: string, context: Record<string, number>): number | null {
  try {
    const ast = parseFormula(formula);
    const result = evaluateAST(ast, context);
    return isFinite(result) ? result : null;
  } catch {
    return null;
  }
}

/**
 * Extract column dependencies from a formula
 */
export function getFormulaDependencies(formula: string): string[] {
  try {
    const tokens = tokenize(formula);
    const deps: string[] = [];
    for (const token of tokens) {
      if (token.type === 'IDENTIFIER') {
        deps.push(token.value as string);
      }
    }
    return [...new Set(deps)];
  } catch {
    return [];
  }
}
